import { useEffect, useRef, useState } from 'react';
import type { BrigadeStation, TurnoutProfile } from '@duty/shared';

/** Restrict a stored-overrides map to just the given stations' ids. */
export function pickStationOverrides(
  overrides: Record<string, TurnoutProfile>,
  stations: readonly BrigadeStation[],
): Record<string, TurnoutProfile> {
  const out: Record<string, TurnoutProfile> = {};
  for (const s of stations) {
    const o = overrides[s.id];
    if (o) out[s.id] = o;
  }
  return out;
}

/**
 * Shared, persisted turn-out defaults (see api/src/functions/turnout-overrides.ts) —
 * once an SME sets a brigade's real depart-station time it becomes the
 * default for every user/scenario, not just this browser session. Falls back
 * to an empty map (seed defaults only) when the API is absent (standalone
 * mode) or unreachable; never blocks rendering on the fetch.
 */
export function useStoredTurnoutOverrides(): {
  storedOverrides: Record<string, TurnoutProfile>;
  saveOverride: (stationId: string, baseMinutes: number) => void;
} {
  const [storedOverrides, setStoredOverrides] = useState<Record<string, TurnoutProfile>>({});

  useEffect(() => {
    let cancelled = false;
    void fetch('/api/turnout-overrides')
      .then((res) => (res.ok ? (res.json() as Promise<{ overrides: TurnoutProfile[] }>) : undefined))
      .then((data) => {
        if (cancelled || !data) return;
        setStoredOverrides(Object.fromEntries(data.overrides.map((o) => [o.stationId, o])));
      })
      .catch(() => {
        // API absent/unreachable — session-only overrides still work, just not shared.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Debounce writes per-station so rapid +/- clicks don't fire a request per click.
  const timers = useRef<Record<string, number>>({});
  const saveOverride = (stationId: string, baseMinutes: number) => {
    setStoredOverrides((prev) => ({
      ...prev,
      [stationId]: { stationId, baseMinutes, provenance: 'sme-entered', capturedAt: new Date().toISOString() },
    }));
    window.clearTimeout(timers.current[stationId]);
    timers.current[stationId] = window.setTimeout(() => {
      void fetch('/api/turnout-overrides', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stationId, baseMinutes }),
      }).catch(() => {
        // Best-effort — the local session override still applies even if the write-through fails.
      });
    }, 800);
  };

  return { storedOverrides, saveOverride };
}
